import { clone } from '../../../webmodule-common/other/clone';
import { compareFrames, isFrameCompareReportEmpty } from '../../../webmodule-common/interop/interfaces/frame-data';
import { customElement, state } from 'lit/decorators.js';
import { EventTemplate } from '../../../webmodule-common/other/ui/events';
import { FabricationJobContainerManager } from '../data/fabrication-job-container';
import { FabricationJobItem, InputUnitOverride } from '../../api/fabrication-job-api';
import { fireQuickErrorToast } from '../../../webmodule-common/other/toast-away';
import { FranchiseeQuoteItemFrameViewForV6 } from '../../dealer-franchisee/quotes/views/v6/franchisee-v6-quote-item-frame-view';
import { getApiFactory } from '../../api/api-injector';
import { getV6QuoteItemForContainer, virtualPathToUrl } from '../../quotes/data/v6/helper-functions';
import { secureFetch } from '../../api/secure-fetch';
import { html, nothing, TemplateResult } from 'lit';
import { LangFabricationJobItemFrameView } from '../../../localization/scripts/fabrication-jobs/views/fabrication-job-item-frame-view-lang';
import { QuoteContainerManager } from '../../quotes/data/quote-container';
import { QuoteItemContainer } from '../../quotes/data/quote-item-container';
import { QuoteItemViewOptions } from '../../quotes/views/quote-item-view';
import { saveWithIndicator } from '../../../webmodule-common/other/save-workflow';
import { toJsonStr } from '../../../webmodule-common/other/blob-converters';
import { reloadFromDataProviderSupport, v6Util } from '../../../webmodule-common/interop/interfaces/v6-config';

export interface FabricationJobItemFrameViewOptions {
  quoteItemViewOptions: QuoteItemViewOptions;
  fabricationJobItem: FabricationJobItem;
  fabricationJobManager: FabricationJobContainerManager;
  quoteManager: QuoteContainerManager;
  quoteItemContainer: QuoteItemContainer;
  supplierId: string;
}

/**
 * Extends the standard V6 frame view to provide per-unit option editing
 * via a tab bar. Tab 0 ("Quoted") shows original options readonly.
 * Tabs 1..N ("Unit 1".."Unit N") show editable copies for site adjustments.
 *
 * Uses a single V6 editor instance (created by the parent) and swaps data
 * by changing what the `quoteItem` getter returns, then refreshing the editor.
 * The V6 editor reads from the QuoteFrameDataProvider dynamically on each render.
 *
 * Saving integrates with the standard DataEntryView save framework:
 * prepareForSave → internalDataChanged → internalSaveData → save indicator.
 */
@customElement('wm-fabrication-job-item-frame-view')
export class FabricationJobItemFrameView extends FranchiseeQuoteItemFrameViewForV6 {
  private fabOptions: FabricationJobItemFrameViewOptions;

  /** 0 = Quoted (readonly), 1..N = Unit 1..N (editable) */
  @state() private activeTabIndex = 0;

  private unitCount = 1;

  /**
   * Per-unit V6QuoteItem snapshots (outer V6QuoteItemExternalStorage wrappers).
   * Index 0 = original quoted data (deep-cloned, never modified - used as the
   * readonly "Quoted" tab and as the baseline for change detection).
   * Index 1..N = editable unit copies, initially cloned from the original or
   * loaded from previously saved overrides.
   */
  private unitSnapshots: (object | null)[] = [];

  /**
   * Per-unit piece-level BOM snapshots, captured alongside unitSnapshots when
   * the user commits the editor for that unit. Persisted as standalone
   * `bom.json` blobs (sibling to the V6 snapshot blob) so the optimizer can
   * read piece-level BOM directly. A null entry means "no bom captured this
   * session for this unit" - we skip that unit on save so existing override
   * blobs on the backend are preserved as-is.
   */
  private unitBoms: (object | null)[] = [];

  /** Tracks which unit tabs have been modified by the user. */
  @state() private unitModified: boolean[] = [];

  /** Tracks which unit tabs are still loading their blob data. */
  @state() private unitLoading: boolean[] = [];

  /** Tracks which unit tabs failed to load their override blob. */
  @state() private unitFetchFailed: boolean[] = [];

  /**
   * Per-unit "user-edited since the last successful save" flag. Distinct from
   * `unitModified` (which is sticky-true for any unit that has an override
   * recorded, including ones loaded from the server). The auto-save on tab
   * switch only fires when at least one unit is dirty by THIS measure - re-
   * saving identical content would otherwise race with the previous save's
   * `loadFromServer` and trip optimistic concurrency.
   */
  private unitDirtySinceSave: boolean[] = [];

  /** The original V6QuoteItem from the quote item container. */
  private originalV6QuoteItem: object | undefined;

  /**
   * Pristine per-unit V6QuoteItem captured the first time any unit tab is
   * opened - V6 processes at quantity=1 context (see the `quantity` override)
   * and caches the resulting container in its `firstLoadContainer`. commit()
   * below reads that cache without triggering an extra round-trip. Serves as
   * the baseline the backend persists on first modification save so later
   * BOM resolution can fall back to it for non-overridden units.
   */
  private pristinePerUnitV6QuoteItem: object | null = null;

  /**
   * Pristine per-unit piece-level BOM captured alongside the V6 snapshot above.
   * Persisted as the baseline BOM blob (sibling to baseline V6 snapshot).
   */
  private pristinePerUnitBom: object | null = null;

  /** Bound handler for V6 attribute changes - fires whenever an attribute is committed. */
  private editorChangeHandler = this.onAttributeChanged.bind(this);
  private saving = false;

  constructor(options: FabricationJobItemFrameViewOptions) {
    super(options.quoteItemViewOptions);
    this.fabOptions = options;
  }

  public async afterConstruction(): Promise<void> {
    // Initialize unit data before the parent creates the V6 editor
    this.originalV6QuoteItem = getV6QuoteItemForContainer(this.fabOptions.quoteItemContainer);
    // Per-unit override editing requires V6QuoteFrameEditor.reloadFromDataProvider,
    // which only exists in v6config v2.2.4+. On older versions fall back to a
    // read-only "Quoted" view (no Unit 1..N tabs, no fetches, no saves).
    const supportsUnitOverrides = reloadFromDataProviderSupport();
    this.unitCount = supportsUnitOverrides ? (this.fabOptions.quoteItemContainer.item.quantity ?? 1) : 0;

    const existingOverrides = supportsUnitOverrides ? (this.fabOptions.fabricationJobItem.unitOverrides ?? []) : [];

    // Index 0 = Quoted tab. Must be a deep clone so V6 editor mutations on unit
    // tabs don't bleed back into the original reference.
    this.unitSnapshots = [this.originalV6QuoteItem ? clone(this.originalV6QuoteItem) : null];
    this.unitBoms = [null];
    this.unitModified = [false];
    this.unitLoading = [false];
    this.unitFetchFailed = [false];
    this.unitDirtySinceSave = [false];

    // Index 1..N = initialize all units with clones so the editor can render
    // immediately. Units with saved overrides are marked as loading - their
    // blob data will be fetched in the background after the component renders.
    const unitsToFetch: { index: number; virtualPath: string }[] = [];
    for (let i = 1; i <= this.unitCount; i++) {
      const existing = existingOverrides.find(o => o.unitIndex === i - 1);
      const hasOverride = !!existing?.v6QuoteItemVirtualPath;
      this.unitSnapshots.push(this.originalV6QuoteItem ? clone(this.originalV6QuoteItem) : null);
      this.unitBoms.push(null);
      this.unitModified.push(hasOverride);
      this.unitLoading.push(hasOverride);
      this.unitFetchFailed.push(false);
      // Existing overrides are NOT dirty-since-save: they're already on the
      // server. Only true user edits in this session flip this.
      this.unitDirtySinceSave.push(false);
      if (hasOverride) {
        unitsToFetch.push({ index: i, virtualPath: existing!.v6QuoteItemVirtualPath! });
      }
    }

    // Start on the Quoted tab (readonly)
    this.activeTabIndex = 0;

    // Let the parent create the V6 editor and render the component first
    await super.afterConstruction();

    // Now fetch blob data for all units with overrides in parallel
    if (unitsToFetch.length > 0) {
      this.fetchUnitOverridesInBackground(unitsToFetch);
    }
  }

  /**
   * Fetches unit override blobs in parallel in the background. As each blob
   * arrives, the unit's snapshot is swapped in and the loading flag is cleared.
   * If the user is currently viewing a tab whose data just loaded, the editor
   * is refreshed to show the fetched data.
   *
   * Concurrency note: these fetches write to unitSnapshots[] concurrently with
   * the tab-switch save path (onTabClick -> internalSaveData). This is safe because:
   * 1. JS is single-threaded - the save loop (internalSaveData lines 317-324) reads
   *    all snapshots synchronously with no await, so no fetch callback can interleave.
   * 2. The unitLoading guard in onTabClick prevents saving the active tab while its
   *    fetch is still in progress.
   * 3. The worst case is a fetch completing during an await in the save path for a
   *    *different* tab - this causes a no-op re-save of already-persisted data,
   *    which the backend's delta merge handles idempotently.
   */
  private fetchUnitOverridesInBackground(units: { index: number; virtualPath: string }[]): void {
    for (const unit of units) {
      this.fetchSingleUnitOverride(unit.index, unit.virtualPath);
    }
  }

  private async fetchSingleUnitOverride(unitIndex: number, virtualPath: string): Promise<void> {
    // Defensive: per-unit override editing requires v6config v2.2.4+. With unitCount = 0
    // this path is unreachable, but the guard protects against future refactors.
    if (!reloadFromDataProviderSupport()) return;
    let fetchOk = false;
    try {
      const url = virtualPathToUrl(virtualPath);
      const response = await secureFetch(url);
      if (response.ok) {
        const json = await response.text();
        this.unitSnapshots[unitIndex] = JSON.parse(json);
        fetchOk = true;
      }
    } catch {
      // Cloned original stays in place for display, but we prevent save from overwriting
    }

    if (!fetchOk) {
      // Mark fetch as failed and clear modified flag to prevent accidental overwrite
      // of the real override with original data during save
      console.warn(
        `[FabricationJobItemFrameView] Failed to fetch unit override for unit ${unitIndex} from ${virtualPath}`
      );
      const modified = [...this.unitModified];
      modified[unitIndex] = false;
      this.unitModified = modified;

      const failed = [...this.unitFetchFailed];
      failed[unitIndex] = true;
      this.unitFetchFailed = failed;
    }

    // Clear loading state (triggers re-render to remove spinner from tab)
    const loading = [...this.unitLoading];
    loading[unitIndex] = false;
    this.unitLoading = loading;

    // If the user is viewing this tab, reload the editor with the fetched data
    if (this.activeTabIndex === unitIndex && this.v6Editor) {
      this.v6Editor.reloadFromDataProvider(this.unitSnapshots[unitIndex]);
      this.v6Editor.resetEditorState();
      this.v6Editor.requestUpdate();
    }
  }

  public override async prepareEditor(): Promise<void> {
    await super.prepareEditor();

    // V6's internal attribute pickers don't bubble generic click/input/change
    // events reliably, so we listen for the custom 'attributechanged' event
    // V6 fires from v6-frame-attribute.doDataChanged when a value is committed.
    // Bubble phase (not capture) so V6's own handler runs first and populates
    // detail.resolver with the server-roundtrip + tracker-update promise.
    if (this.v6Editor?.ui) {
      this.v6Editor.ui.addEventListener('attributechanged', this.editorChangeHandler);
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.v6Editor?.ui) {
      this.v6Editor.ui.removeEventListener('attributechanged', this.editorChangeHandler);
    }
  }

  private async onAttributeChanged(e: Event): Promise<void> {
    if (this.activeTabIndex === 0 || !this.v6Editor) return;

    // Yield once so V6's _attributesChanged listener (which assigns
    // detail.resolver synchronously) runs before we read it. Listener
    // registration order between our hook and V6's connectedCallback is
    // not guaranteed.
    await Promise.resolve();

    // Wait for V6 to finish processing the change (server roundtrip + power-
    // user attribute tracker update). Only after the resolver settles is
    // v6Editor.changed - which reads modifiedAttributes() - the source of
    // truth.
    const resolver = (e as CustomEvent).detail?.resolver as Promise<void> | undefined;
    if (resolver) {
      try {
        await resolver;
      } catch {
        /* error already surfaced by V6 */
      }
    }

    if (!this.v6Editor || this.activeTabIndex === 0) return;

    const isChanged = this.v6Editor.changed;
    if (isChanged !== this.unitModified[this.activeTabIndex]) {
      const modified = [...this.unitModified];
      modified[this.activeTabIndex] = isChanged;
      this.unitModified = modified;
    }
    if (isChanged) {
      this.unitDirtySinceSave[this.activeTabIndex] = true;
    }
  }

  // --- QuoteFrameDataProvider overrides ---

  override get quoteItem(): object | undefined {
    return (this.unitSnapshots[this.activeTabIndex] ?? this.originalV6QuoteItem) as object | undefined;
  }

  override get readonly(): boolean {
    if (this.activeTabIndex === 0) return true;
    if (this.unitLoading[this.activeTabIndex]) return true;
    if (this.unitFetchFailed[this.activeTabIndex]) return true;
    return this.fabOptions.fabricationJobManager.isReadOnly();
  }

  /**
   * Quoted tab shows total quantity. Unit tabs show 1 since each unit is a single item.
   */
  override get quantity(): number {
    if (this.activeTabIndex === 0) return this.unitCount;
    return 1;
  }

  /**
   * Override properties template to show quantity as 1 (readonly) for unit tabs.
   * The parent's propertiesTemplate reads quantity from the dataTracker bound to
   * the quote item's total quantity, which is incorrect for individual units.
   */
  override propertiesTemplate(): TemplateResult {
    if (this.activeTabIndex === 0) return super.propertiesTemplate();

    // For unit tabs, render a simplified properties section with quantity = 1
    const item = this.fabOptions.quoteItemContainer.item;
    return html`
      <div class="form-one-col quoteItemFrameProperties">
        <div class="row attributeContents">
          <div class="v6config-group">
            <div class="v6config-group-header">${LangFabricationJobItemFrameView.propertiesHeader()}</div>
            <div class="row mb-1">
              <label class="col-sm-4 col-form-label">${LangFabricationJobItemFrameView.locationLabel()}</label>
              <div class="col-sm-8"><input class="form-control" readonly .value=${item.title ?? ''} /></div>
            </div>
            <div class="row mb-1">
              <label class="col-sm-4 col-form-label">${LangFabricationJobItemFrameView.descriptionLabel()}</label>
              <div class="col-sm-8"><input class="form-control" readonly .value=${item.description ?? ''} /></div>
            </div>
            <div class="row mb-1">
              <label class="col-sm-4 col-form-label">${LangFabricationJobItemFrameView.quantityLabel()}</label>
              <div class="col-sm-8"><input class="form-control" readonly .value=${'1'} /></div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Override validation to skip the parent's quantity DOM lookup on unit tabs.
   * The parent reads quantity from a dataTracker-bound input element that doesn't
   * exist in our simplified unit tab properties template.
   */
  protected override getValidationErrors(): string[] {
    if (this.activeTabIndex > 0) return []; // No validation needed for unit overrides
    return super.getValidationErrors();
  }

  // --- Save framework overrides ---

  /**
   * Override isDataReadonly for the save framework. The parent checks
   * quoteManager.isReadonly() which is true (forceLockout), but we need
   * the save framework to run prepareForSave/internalSaveData for unit overrides.
   * Readonly is determined by job state, not quote state.
   */
  override isDataReadonly(): boolean {
    if (this.activeTabIndex === 0) return true;
    if (this.unitLoading[this.activeTabIndex]) return true;
    if (this.unitFetchFailed[this.activeTabIndex]) return true;
    return this.fabOptions.fabricationJobManager.isReadOnly();
  }

  /**
   * Capture the current V6 editor state into the active unit's snapshot.
   * Does NOT call super - we don't want the standard quote item save path.
   */
  override async prepareForSave(): Promise<void> {
    await this.captureCurrentEditorState();
  }

  /**
   * Returns true if any unit has user edits since the last successful save.
   * Drives the framework's "you have unsaved changes" prompt and the
   * tab-switch auto-save gate. Distinct from `unitModified`, which is sticky
   * for any unit that has an override (including ones already on the server).
   */
  override internalDataChanged(): boolean {
    return this.unitDirtySinceSave.some((d, i) => i > 0 && d);
  }

  /**
   * Save unit overrides to the fabrication API.
   * Only modified units are included in the payload. The fabrication job's
   * recordVersion is sent for optimistic concurrency protection.
   */
  override async internalSaveData(): Promise<boolean> {
    try {
      const overrides: InputUnitOverride[] = [];
      for (let i = 1; i <= this.unitCount; i++) {
        // Only send units we actually captured this session (unitBoms[i] is
        // populated by captureCurrentEditorState / first-load capture). Units
        // the user opened but didn't touch keep their existing override blobs
        // intact via the backend's preserve-not-mentioned merge semantics.
        if (
          this.unitModified[i] &&
          this.unitSnapshots[i] &&
          this.unitBoms[i] &&
          !this.unitLoading[i] &&
          !this.unitFetchFailed[i]
        ) {
          overrides.push({
            unitIndex: i - 1,
            v6QuoteItemJson: toJsonStr(this.unitSnapshots[i]) ?? null,
            bomJson: toJsonStr(this.unitBoms[i]) ?? null
          });
        }
      }

      // On the first modification save for this job item, attach the pristine
      // quantity=1 snapshot (and its paired BOM) captured on first unit-tab
      // entry so the backend can persist them as the per-unit baseline blobs.
      // Write-once on the backend, so sending on subsequent saves is a no-op -
      // we still gate here on "baseline not yet recorded" to avoid sending
      // payload unnecessarily.
      const baselineAlreadyRecorded = !!this.fabOptions.fabricationJobItem.baselineV6QuoteItemVirtualPath;
      const sendingBaseline = !baselineAlreadyRecorded && overrides.length > 0 && this.pristinePerUnitV6QuoteItem;
      const pristineV6QuoteItemJson = sendingBaseline ? (toJsonStr(this.pristinePerUnitV6QuoteItem) ?? null) : null;
      const pristineBomJson =
        sendingBaseline && this.pristinePerUnitBom ? (toJsonStr(this.pristinePerUnitBom) ?? null) : null;

      const api = getApiFactory().fabricationJob();
      const result = await api.updateFabricationJobItemUnitOverrides({
        fabricationJobItemId: this.fabOptions.fabricationJobItem.id,
        fabricationJobRecordVersion: this.fabOptions.fabricationJobManager.fabricationJob.recordVersion,
        unitOverrides: overrides,
        pristineV6QuoteItemJson,
        pristineBomJson
      });

      if (result) {
        // All units we just sent are now persisted - clear the dirty flag so
        // tab-switch auto-save won't re-send identical content. Units we
        // skipped (no captured bom) weren't in the payload, but they also
        // weren't user-edited this session, so leaving their flag is fine.
        this.unitDirtySinceSave = this.unitDirtySinceSave.map(() => false);

        // Reload the full job + items from the server so the container has fresh data.
        // This ensures the items table's overrides column reflects the saved changes
        // when the user navigates back to the Job Items tab.
        await this.fabOptions.fabricationJobManager.loadFromServer();
        return true;
      }
      return false;
    } catch (e) {
      console.error('Failed to save unit overrides', e);
      fireQuickErrorToast(LangFabricationJobItemFrameView.saveUnitOverridesFailed());
      return false;
    }
  }

  /**
   * Use the simple save indicator (spinning icon), not the full progress modal.
   * Our save is a single API call, not a multi-step process like quote item saves.
   */
  override displaySaveModal(): boolean {
    return false;
  }

  // --- UI ---

  protected override bodyTemplate(): EventTemplate {
    return html`
      ${this.renderTabBar()} ${this.renderOptimizationLockedBanner()}
      <div class="fab-unit-panel-header">
        ${this.activeTabIndex === 0
          ? LangFabricationJobItemFrameView.quotedMeasurementsHeader()
          : `${LangFabricationJobItemFrameView.onSiteMeasurementsHeader()} #${this.activeTabIndex}`}
      </div>
      <div class="fab-unit-body">${super.bodyTemplate()}</div>
    `;
  }

  // Why a banner when the V6 editor already shows a lock icon top-right?
  // That icon is easy to miss and gives no reason - users opened a tab
  // and saw inputs disabled with no explanation.
  private renderOptimizationLockedBanner(): EventTemplate {
    if (!this.fabOptions.fabricationJobManager.isReadOnly()) return html``;
    return html`
      <div class="fab-unit-readonly-banner">
        <i class="fa-solid fa-lock"></i>
        <span>${LangFabricationJobItemFrameView.optimizationLockedReadonlyBanner()}</span>
      </div>
    `;
  }

  private renderTabBar(): EventTemplate {
    // No tab bar when per-unit overrides are unsupported by the active v6config -
    // there is only one logical tab (Quoted) and the per-unit header handles it.
    if (!reloadFromDataProviderSupport()) return html``;

    const tabs: { label: string; index: number }[] = [{ label: LangFabricationJobItemFrameView.quotedTab(), index: 0 }];
    for (let i = 1; i <= this.unitCount; i++) {
      tabs.push({ label: `#${i}`, index: i });
    }

    return html`
      <div class="fab-unit-panel">
        <ul class="nav nav-tabs fab-unit-tabs">
          ${tabs.map(tab => {
            const isActive = this.activeTabIndex === tab.index;
            const isQuoted = tab.index === 0;
            const isModified = tab.index > 0 && this.unitModified[tab.index];
            const isLoading = tab.index > 0 && this.unitLoading[tab.index];
            const isFetchFailed = tab.index > 0 && this.unitFetchFailed[tab.index];
            const linkClass = `nav-link fab-unit-tab-link ${isQuoted ? 'fab-unit-tab-quoted' : ''} ${isActive ? 'active' : ''}`;
            return html`
              <li class="nav-item">
                <a class="${linkClass}" href="javascript:void(0)" @click=${() => this.onTabClick(tab.index)}>
                  <span class="fab-unit-tab-content"
                    >${tab.label}${isLoading
                      ? html` <i class="fa-solid fa-spinner fa-spin fab-unit-tab-loading-icon"></i>`
                      : isFetchFailed
                        ? html` <i class="fa-solid fa-triangle-exclamation fab-unit-tab-error-icon"></i>`
                        : isModified
                          ? html` <i class="fa-solid fa-pen fab-unit-tab-modified-icon"></i>`
                          : nothing}</span
                  >
                </a>
              </li>
            `;
          })}
        </ul>
      </div>
    `;
  }

  // --- Tab switching ---

  private async onTabClick(tabIndex: number): Promise<void> {
    if (tabIndex === this.activeTabIndex) return;
    if (!this.v6Editor) return;
    if (this.saving) return;
    // Defensive: per-unit override editing requires v6config v2.2.4+. With the tab
    // bar hidden on older versions this is unreachable, but guard against refactors.
    if (!reloadFromDataProviderSupport()) return;

    // Capture and save directly when leaving an editable unit tab.
    // We bypass performAutoSave() because it goes through the resource lock system
    // which is already held by the parent TabManager tab - causing "Already Locked" errors.
    // The framework overrides (prepareForSave/internalSaveData) still handle saves
    // when the tab itself is closed via allowPageSwitch().
    //
    // Auto-save is gated on `unitDirtySinceSave` (true only when the user
    // actually edited a unit in this session) instead of `unitModified`
    // (sticky-true for any unit with a recorded override). Without this gate,
    // tab-switching after a successful explicit save would trigger a no-op
    // re-save that races the previous save's loadFromServer and trips
    // optimistic concurrency on the FabricationJob row.
    if (
      this.activeTabIndex > 0 &&
      !this.unitLoading[this.activeTabIndex] &&
      !this.unitFetchFailed[this.activeTabIndex] &&
      !this.fabOptions.fabricationJobManager.isReadOnly()
    ) {
      const captured = await this.captureCurrentEditorState();
      if (!captured) return; // Capture failed - stay on current tab so user can retry
      if (this.unitDirtySinceSave.some((d, i) => i > 0 && d)) {
        this.saving = true;
        try {
          const saved = await saveWithIndicator(async () => await this.internalSaveData());
          if (!saved) return; // Save failed, stay on tab
        } finally {
          this.saving = false;
        }
      }
    }

    // Switch tab - the quoteItem getter now returns the new unit's snapshot
    this.activeTabIndex = tabIndex;

    // Swap the V6 editor's data to match the new tab. reloadFromDataProvider
    // extracts the inner V6QuoteItem from the outer storage wrapper, updates
    // the editor service, preserves the thumbnail, and refreshes the view.
    this.v6Editor.reloadFromDataProvider(this.unitSnapshots[tabIndex]);

    // Reset modification tracking since we loaded different data
    this.v6Editor.resetEditorState();

    // First time landing on ANY unit tab for this frame view AND no baseline is
    // recorded yet on the job item - capture the pristine quantity=1 V6 response
    // as the per-unit baseline. The editor service performs a V6 round-trip
    // automatically when the unit editor is shown (the `quantity` override
    // returns 1 for unit tabs) and caches the resulting V6QuoteItemContainer in
    // `firstLoadContainer`. commit() below reads that cache - no extra round-trip.
    //
    // Retry behaviour: if this capture fails (throw, or no quoteItem in the
    // result), pristinePerUnitV6QuoteItem stays null and the next tab switch
    // runs this block again. Combined with internalSaveData's gate (send
    // pristine whenever the backend still reports baseline not recorded AND a
    // pristine snapshot is in memory), this covers both failure modes the
    // baseline write-path cares about:
    //   - capture failed -> retried on subsequent tab switch -> sent on next save
    //   - backend committed DB pointer but blob commit failed -> read-side
    //     self-heal nulls the pointer -> UI sees baselineAlreadyRecorded=false
    //     after reload -> pristine captured and sent again.
    const baselineAlreadyRecorded = !!this.fabOptions.fabricationJobItem.baselineV6QuoteItemVirtualPath;
    if (tabIndex > 0 && !this.pristinePerUnitV6QuoteItem && !baselineAlreadyRecorded) {
      try {
        const firstLoadResult = await this.v6Editor.commit();
        if (firstLoadResult?.quoteItem) {
          this.pristinePerUnitV6QuoteItem = firstLoadResult.quoteItem as object;
          // Capture bom alongside the v6 snapshot - the backend persists them
          // as paired write-once baseline blobs. Bom may be undefined if the
          // configure-quote response didn't include it (older callers); the
          // backend will then skip the BOM blob and surface a re-save prompt
          // when the optimizer runs.
          this.pristinePerUnitBom = (firstLoadResult.bom as object | undefined) ?? null;
          this.unitSnapshots[tabIndex] = firstLoadResult.quoteItem as object;
          this.unitBoms[tabIndex] = (firstLoadResult.bom as object | undefined) ?? null;
          this.v6Editor.resetEditorState();
        } else {
          console.warn('Pristine per-unit snapshot capture returned no quoteItem; will retry on next tab switch');
          fireQuickErrorToast(LangFabricationJobItemFrameView.capturePristineBaselineFailed());
        }
      } catch (e) {
        console.error('Failed to capture pristine per-unit snapshot', e);
        fireQuickErrorToast(LangFabricationJobItemFrameView.capturePristineBaselineFailed());
      }
    }

    // Trigger re-render
    this.v6Editor.requestUpdate();
    this.requestUpdate();
  }

  /**
   * Captures the current V6 editor state by committing the editor, comparing
   * the result against the original quoted data using compareFrames, and
   * marking the active unit as modified if meaningful differences exist.
   */
  private async captureCurrentEditorState(): Promise<boolean> {
    if (!this.v6Editor || this.activeTabIndex === 0) return true;

    try {
      const commitResult = await this.v6Editor.commit();
      if (commitResult?.quoteItem) {
        this.unitSnapshots[this.activeTabIndex] = commitResult.quoteItem as object;
        // Capture bom alongside the v6 snapshot so internalSaveData can send
        // both to the backend (paired blob persistence).
        this.unitBoms[this.activeTabIndex] = (commitResult.bom as object | undefined) ?? null;

        // Compare the unit's frame data with the original to determine if there
        // are real changes. Uses compareFrames (the same utility the parent class
        // uses for frame diff reporting) instead of raw JSON comparison.
        const unitFrameData = commitResult.quoteItem ? v6Util().getFrameData(commitResult.quoteItem as object) : null;
        const originalFrameData = this.unitSnapshots[0] ? v6Util().getFrameData(this.unitSnapshots[0] as object) : null;
        const diff = compareFrames(originalFrameData, unitFrameData, { excludeHidden: true });
        const isChanged = !isFrameCompareReportEmpty(diff);

        const modified = [...this.unitModified];
        modified[this.activeTabIndex] = isChanged;
        this.unitModified = modified;

        // Belt-and-braces dirty-since-save tracking: if compareFrames detected
        // real changes, the user made edits since the last save. This catches
        // the case where the user edits and switches tabs faster than the
        // 300ms onEditorInteraction debounce can fire - so the auto-save still
        // triggers and the optimizer result invalidates correctly. Reuses
        // isChanged rather than a separate JSON.stringify deep-compare.
        if (isChanged) {
          this.unitDirtySinceSave[this.activeTabIndex] = true;
        }
      }
      return true;
    } catch (e) {
      console.error('Failed to capture editor state', e);
      fireQuickErrorToast(LangFabricationJobItemFrameView.captureEditorStateFailed());

      // Prevent stale unitModified from triggering a save with old snapshot data
      const modified = [...this.unitModified];
      modified[this.activeTabIndex] = false;
      this.unitModified = modified;

      return false;
    }
  }
}
